import { el, btn } from '@/ui/dom';
import { nativeShims } from '@/native/shims';

/**
 * LobbyScreen.ts — the online waiting room (M30). Replaces the old in-HUD
 * "WAITING…" banner over a frozen grid. Three faces:
 *   - invite:     friend challenge / rematch — room code, Copy Invite Link, a
 *                 live "waiting (1/2)" status, and (host only) Start vs AI now.
 *   - connecting: vs-AI / handshake — a brief "connecting…" (the race starts at
 *                 once), with a Cancel.
 *   - error:      the connection failed or the room was full.
 *
 * Its background is opaque (unlike the other menu scrims) because it sits over a
 * live race scene + HUD — it must fully cover them. M113: it wears the online
 * night-highway photo (screen-onlinebg, opaque base colour underneath) so the
 * hub → setup → lobby flow reads as one continuous place.
 */
export interface LobbyCallbacks {
  /** Host (slot 0) pressed "Start vs AI now" → Game sends {t:'start'}. */
  onStartAi: () => void;
  /** Left the lobby (back to the online hub). */
  onCancel: () => void;
}

export class LobbyScreen {
  private root: HTMLDivElement;
  private panel: HTMLDivElement;
  private statusEl: HTMLDivElement | null = null;
  private infoEl: HTMLDivElement | null = null;
  private startBtn: HTMLButtonElement | null = null;
  private copyBtn: HTMLButtonElement | null = null;
  private link = '';
  private copyResetTimer: number | null = null;

  constructor(
    parent: HTMLElement,
    private cb: LobbyCallbacks,
  ) {
    this.root = el('div', 'screen screen-lobby screen-onlinebg', parent);
    this.root.style.display = 'none';
    this.panel = el('div', 'panel lobby-panel', this.root);
  }

  /** Friend challenge / rematch: code + copy link + live waiting status. */
  showInvite(opts: { code: string; link: string; rematch?: boolean; info?: string }): void {
    this.clearCopyTimer();
    this.link = opts.link;
    this.panel.replaceChildren();

    this.addHead('CHALLENGE FRIENDS', opts.rematch ? 'REMATCH' : 'INVITE A FRIEND');
    this.infoEl = el('div', 'lobby-info', this.panel);
    this.infoEl.textContent = opts.info ?? '';

    el('div', 'lobby-label', this.panel).textContent = 'ROOM CODE';
    el('div', 'lobby-code', this.panel).textContent = opts.code;
    el('div', 'lobby-link', this.panel).textContent = opts.link;

    this.copyBtn = btn('COPY INVITE LINK', 'btn btn-primary', this.panel, () => {
      void this.copy();
    });

    this.statusEl = el('div', 'lobby-status', this.panel);
    this.statusEl.textContent = opts.rematch
      ? 'Waiting for your friend to rematch…'
      : 'Waiting for your friend to join…';

    this.startBtn = btn('START NOW', 'btn btn-secondary', this.panel, () => this.cb.onStartAi());
    this.startBtn.style.display = 'none'; // revealed once we know we're the host

    btn('CANCEL', 'btn btn-danger-ghost', this.panel, () => this.cb.onCancel());
    this.root.style.display = '';
  }

  /** M92: the editorial header — gold rule-flanked kicker + chrome title
   *  (the home-screen language) on every lobby face. */
  private addHead(kicker: string, title: string): void {
    const kick = el('div', 'mhead-kicker', this.panel);
    el('span', 'mhead-rule', kick);
    el('span', 'mhead-kicker-text', kick).textContent = kicker;
    el('span', 'mhead-rule', kick);
    el('div', 'lobby-title mhead-chrome', this.panel).textContent = title;
  }

  /** Vs-AI / handshake: no invite, just a brief connecting note. The default
   *  title never names AI (M141 owner rule: online play never shows AI). */
  showConnecting(title = 'ONLINE RACE'): void {
    this.clearCopyTimer();
    this.link = '';
    this.copyBtn = null;
    this.panel.replaceChildren();

    this.addHead('PLAY ONLINE', title);
    this.infoEl = null;
    this.statusEl = el('div', 'lobby-status', this.panel);
    this.statusEl.textContent = 'Connecting to the server…';

    this.startBtn = btn('START NOW', 'btn btn-secondary', this.panel, () => this.cb.onStartAi());
    this.startBtn.style.display = 'none';

    btn('CANCEL', 'btn btn-danger-ghost', this.panel, () => this.cb.onCancel());
    this.root.style.display = '';
  }

  /** Update the live "(filled/total)" count from the server's lobby messages. */
  setWaiting(humans: number, cap: number): void {
    if (this.statusEl) {
      const who = cap > 2 ? 'players' : 'your friend';
      this.statusEl.textContent = `Waiting for ${who}…  (${humans}/${cap})`;
    }
  }

  /** The room's city + racer count, shown under the title (e.g. "Tokyo · 6 racers"). */
  setInfo(text: string): void {
    if (this.infoEl) this.infoEl.textContent = text;
  }

  /** Only the host (slot 0) may force-start, so the button stays hidden otherwise. */
  setCanStart(canStart: boolean): void {
    if (this.startBtn) this.startBtn.style.display = canStart ? '' : 'none';
  }

  showError(reason: 'full' | 'error' | 'disconnect' | 'outdated'): void {
    this.clearCopyTimer();
    this.panel.replaceChildren();
    this.addHead(
      'PLAY ONLINE',
      reason === 'full'
        ? 'RACE IS FULL'
        : reason === 'outdated'
          ? 'UPDATE AVAILABLE'
          : 'CONNECTION FAILED',
    );
    // M111: in the app shell "reload the page" is meaningless — the fix is a
    // store update (and until it clears review, online play is what's gated;
    // offline racing keeps working).
    const outdatedCopy = nativeShims.platform
      ? `A new version of the game just shipped. Update Dribble Racer ${
          nativeShims.platform === 'ios' ? 'in the App Store' : 'on Google Play'
        } to race online.`
      : 'A new version of the game just shipped. Reload the page to update, then race on.';
    el('div', 'lobby-status', this.panel).textContent =
      reason === 'full'
        ? 'That race is already full or under way. Start a new one.'
        : reason === 'outdated'
          ? outdatedCopy
          : 'Could not reach the server. Check your connection and try again.';
    btn('BACK', 'btn btn-primary', this.panel, () => this.cb.onCancel());
    this.root.style.display = '';
  }

  private async copy(): Promise<void> {
    const btnEl = this.copyBtn;
    try {
      // M111: the native Clipboard plugin first (WKWebView's async clipboard
      // is unreliable); the web keeps navigator.clipboard.
      if (nativeShims.copyText) await nativeShims.copyText(this.link);
      else await navigator.clipboard.writeText(this.link);
      if (btnEl) {
        btnEl.textContent = 'COPIED!';
        this.copyResetTimer = window.setTimeout(() => {
          if (this.copyBtn) this.copyBtn.textContent = 'COPY INVITE LINK';
        }, 1600);
      }
    } catch {
      // Clipboard blocked (insecure context / denied) — the link is on screen, so
      // the user can still copy it by hand.
      if (btnEl) btnEl.textContent = 'COPY FAILED. COPY THE LINK ABOVE';
    }
  }

  private clearCopyTimer(): void {
    if (this.copyResetTimer !== null) {
      window.clearTimeout(this.copyResetTimer);
      this.copyResetTimer = null;
    }
  }

  /** M140: is the screen up? (the crew invite pop-up's "safe moment" check) */
  get isVisible(): boolean {
    return this.root.style.display !== 'none';
  }

  hide(): void {
    this.clearCopyTimer();
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.clearCopyTimer();
    this.root.remove();
  }
}
